import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import TrainerMessengerPreview from "./TrainerMessengerPreview";
import ScenarioAnalytics from "../ScenarioAnalytics";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import {
  getGame,
  saveGameSettings,
  saveTrainerQuestions,
  loadTrainerVideo,
  loadTrainerImage,
  loadPoster,
  getProfile,
} from "../../redux/asyncThunk/userActions";
import { sanitizePlainText } from "../../utils/inputValidation";
import { getServerMediaUrl } from "../../utils/mediaUrl";
import {
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../../utils/projectAccess";
import { readChatAccessGateFromSettings } from "../../utils/chatAccessGate";
import ProjectTeamAccess from "../ProjectTeamAccess";
import {
  readAvailableMessengersFromSettings,
} from "../ProjectMessengerAvailability";
import { AUTH_FIELD_SX } from "../../pages/AuthLayout";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import MaxVideoLimitHint from "../MaxVideoLimitHint";
import "./trainer-editor.css";

const { addMessage, showDialog } = userSlice;
const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

function createEmptyQuestion() {
  const id = `q-${Date.now()}`;
  return {
    id,
    questionMode: "text",
    questionText: "",
    hasVideo: false,
    hasImage: false,
    screenpath: "",
    imagepath: "",
    answers: [
      { id: `${id}-a1`, label: "", isCorrect: false },
      { id: `${id}-a2`, label: "", isCorrect: false },
    ],
  };
}

function formatQuestionCount(count) {
  const n = Number(count) || 0;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return `${n} вопросов`;
  if (mod10 === 1) return `${n} вопрос`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} вопроса`;
  return `${n} вопросов`;
}

function getQuestionMediaMode(question) {
  if (question?.questionMode === "video" || question?.questionMode === "image") {
    return question.questionMode;
  }
  if (question?.questionMode === "text") return "text";
  if (question?.hasVideo) return "video";
  if (question?.hasImage) return "image";
  return "text";
}

function hasQuestionContent(question) {
  const mediaMode = getQuestionMediaMode(question);
  return Boolean(
    question?.questionText?.trim() ||
      (mediaMode === "video" && question?.hasVideo) ||
      (mediaMode === "image" && question?.hasImage),
  );
}

function isQuestionComplete(question) {
  if (!hasQuestionContent(question)) return false;

  const filledAnswers = (question.answers ?? []).filter((answer) =>
    answer.label?.trim(),
  );
  if (!filledAnswers.length) return false;
  return filledAnswers.some((answer) => answer.isCorrect);
}

function questionListLabel(question) {
  const text = question?.questionText?.trim();
  if (text) return text;

  const mediaMode = getQuestionMediaMode(question);
  if (mediaMode === "video") {
    return question?.hasVideo ? "Видео-вопрос" : "Видео не загружено";
  }
  if (mediaMode === "image") {
    return question?.hasImage ? "Вопрос с картинкой" : "Картинка не загружена";
  }
  return "Без текста";
}

export default function TrainerEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game) ?? initialGame;
  const user = useSelector((state) => state.user?.data?.user);
  const profile = useSelector((state) => state.user?.profile);

  const [view, setView] = useState("editor");
  const [title, setTitle] = useState(game?.title ?? "");
  const [description, setDescription] = useState(game?.description ?? "");
  const [questions, setQuestions] = useState(game?.trainerQuestions ?? []);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [settings, setSettings] = useState({
    projectStatus: getProjectStatusFromSettings(game?.settings),
    completion_text: game?.settings?.completion_text ?? "",
    author_telegram: game?.settings?.author_telegram ?? "",
    allow_replay: game?.settings?.allow_replay !== false,
  });
  const [availableMessengers, setAvailableMessengers] = useState(
    readAvailableMessengersFromSettings(game?.settings),
  );
  const [chatAccessGate, setChatAccessGate] = useState(
    readChatAccessGateFromSettings(game?.settings),
  );

  const questionsSaveTimerRef = useRef(null);
  const settingsSaveTimerRef = useRef(null);
  const lastSavedQuestionsRef = useRef(JSON.stringify(game?.trainerQuestions ?? []));
  const lastSavedSettingsRef = useRef("");

  const activeQuestion = useMemo(
    () => questions.find((q) => q.id === activeQuestionId) ?? null,
    [questions, activeQuestionId],
  );
  const activeQuestionMediaMode = getQuestionMediaMode(activeQuestion);

  useEffect(() => {
    if (user?.id && !profile?.login) {
      dispatch(getProfile({ id: user.id }));
    }
  }, [dispatch, user?.id, profile?.login]);

  const buildSettingsSnapshot = useCallback(
    () =>
      JSON.stringify({
        title: title.trim(),
        description,
        settings,
        availableMessengers,
        chatAccessGate,
      }),
    [title, description, settings, availableMessengers, chatAccessGate],
  );

  useEffect(() => {
    if (!game) return;
    const nextTitle = game.title ?? "";
    setTitle(nextTitle);
    setDescription(game.description ?? "");
    setSettings({
      projectStatus: getProjectStatusFromSettings(game.settings),
      completion_text: game.settings?.completion_text ?? "",
      author_telegram: game.settings?.author_telegram ?? "",
      allow_replay: game.settings?.allow_replay !== false,
    });
    setAvailableMessengers(readAvailableMessengersFromSettings(game.settings));
    setChatAccessGate(readChatAccessGateFromSettings(game.settings));
    lastSavedSettingsRef.current = JSON.stringify({
      title: nextTitle.trim(),
      description: game.description ?? "",
      settings: {
        projectStatus: getProjectStatusFromSettings(game.settings),
        completion_text: game.settings?.completion_text ?? "",
        author_telegram: game.settings?.author_telegram ?? "",
        allow_replay: game.settings?.allow_replay !== false,
      },
      availableMessengers: readAvailableMessengersFromSettings(game.settings),
      chatAccessGate: readChatAccessGateFromSettings(game.settings),
    });
  }, [game]);

  useEffect(() => {
    if (!game?._id) return;
    const nextQuestions = game.trainerQuestions ?? [];
    setQuestions(nextQuestions);
    lastSavedQuestionsRef.current = JSON.stringify(
      nextQuestions.filter(isQuestionComplete),
    );
  }, [game?._id, game?.trainerQuestions]);

  const persistQuestions = useCallback(
    async (questionsToSave) => {
      if (!game?._id) return;
      const trimmedTitle = title.trim();
      const completeQuestions = questionsToSave.filter(isQuestionComplete);
      try {
        await dispatch(
          saveTrainerQuestions({
            gameId: game._id,
            trainerTheme: trimmedTitle,
            questions: completeQuestions,
          }),
        ).unwrap();
        lastSavedQuestionsRef.current = JSON.stringify(completeQuestions);
      } catch {
        dispatch(addMessage({ type: "error", message: "Не удалось сохранить вопросы" }));
      }
    },
    [dispatch, game?._id, title],
  );

  const persistSettings = useCallback(async () => {
    if (!game?._id) return;
    const trimmedTitle = title.trim();
    try {
      await dispatch(
        saveGameSettings({
          game: game._id,
          title: trimmedTitle,
          description,
          program: trimmedTitle,
          category: "",
          trainerTheme: trimmedTitle,
          settings: mergeGameSettings(game?.settings, {
            ...settingsFromProjectStatus(settings.projectStatus, game?.settings),
            contentType: "trainer",
            completion_text: settings.completion_text,
            author_telegram: settings.author_telegram,
            allow_replay: settings.allow_replay,
            availableMessengers,
            ...chatAccessGate,
          }),
        }),
      ).unwrap();
      lastSavedSettingsRef.current = buildSettingsSnapshot();
    } catch {
      dispatch(addMessage({ type: "error", message: "Не удалось сохранить настройки" }));
    }
  }, [
    dispatch,
    game?._id,
    game?.settings,
    title,
    description,
    settings,
    availableMessengers,
    chatAccessGate,
    buildSettingsSnapshot,
  ]);

  useEffect(() => {
    const completeQuestions = questions.filter(isQuestionComplete);
    const serialized = JSON.stringify(completeQuestions);
    if (serialized === lastSavedQuestionsRef.current || !game?._id) return;

    clearTimeout(questionsSaveTimerRef.current);
    questionsSaveTimerRef.current = setTimeout(() => {
      persistQuestions(questions);
    }, 800);

    return () => clearTimeout(questionsSaveTimerRef.current);
  }, [questions, game?._id, persistQuestions]);

  useEffect(() => {
    const serialized = buildSettingsSnapshot();
    if (serialized === lastSavedSettingsRef.current || !game?._id) return;

    clearTimeout(settingsSaveTimerRef.current);
    settingsSaveTimerRef.current = setTimeout(() => {
      persistSettings();
    }, 800);

    return () => clearTimeout(settingsSaveTimerRef.current);
  }, [
    title,
    description,
    settings,
    availableMessengers,
    chatAccessGate,
    game?._id,
    buildSettingsSnapshot,
    persistSettings,
  ]);

  const updateActiveQuestion = (patch) => {
    if (!activeQuestionId) return;
    setQuestions((prev) =>
      prev.map((q) => (q.id === activeQuestionId ? { ...q, ...patch } : q)),
    );
  };

  const updateAnswer = (answerId, patch) => {
    if (!activeQuestion) return;
    updateActiveQuestion({
      answers: activeQuestion.answers.map((a) =>
        a.id === answerId ? { ...a, ...patch } : a,
      ),
    });
  };

  const addAnswer = () => {
    if (!activeQuestion || activeQuestion.answers.length >= 4) return;
    updateActiveQuestion({
      answers: [
        ...activeQuestion.answers,
        {
          id: `${activeQuestion.id}-a${activeQuestion.answers.length + 1}`,
          label: "",
          isCorrect: false,
        },
      ],
    });
  };

  const handleAddQuestion = async () => {
    if (activeQuestion && !isQuestionComplete(activeQuestion)) {
      dispatch(
        addMessage({
          type: "error",
          message:
            "Добавьте текст, видео или картинку, заполните ответ и отметьте хотя бы один правильный вариант",
        }),
      );
      return;
    }

    clearTimeout(questionsSaveTimerRef.current);
    await persistQuestions(questions);
    const next = createEmptyQuestion();
    setQuestions((prev) => [...prev, next]);
    setActiveQuestionId(next.id);
  };

  const handleViewChange = async (nextView) => {
    if (view === "settings" && nextView !== "settings") {
      clearTimeout(settingsSaveTimerRef.current);
      await persistSettings();
    }
    setView(nextView);
  };

  const handleDeleteQuestion = (questionId) => {
    setQuestions((prev) => prev.filter((q) => q.id !== questionId));
    if (activeQuestionId === questionId) {
      setActiveQuestionId(null);
    }
    setQuestionToDelete(null);
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeQuestion || !user?.id || !game?._id) return;

    setIsUploadingVideo(true);
    setVideoUploadProgress(0);

    dispatch(
      loadTrainerVideo({
        userId: user.id,
        gameId: game._id,
        questionId: activeQuestion.id,
        file,
        onProgress: (percent) => setVideoUploadProgress(percent),
      }),
    )
      .then((result) => {
        if (result.payload?.trainerQuestions) {
          lastSavedQuestionsRef.current = JSON.stringify(result.payload.trainerQuestions);
          setQuestions(result.payload.trainerQuestions);
        }
      })
      .finally(() => {
        setIsUploadingVideo(false);
        setVideoUploadProgress(0);
        event.target.value = "";
      });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeQuestion || !user?.id || !game?._id) return;

    dispatch(
      loadTrainerImage({
        userId: user.id,
        gameId: game._id,
        questionId: activeQuestion.id,
        file,
      }),
    ).then((result) => {
      if (result.payload?.trainerQuestions) {
        lastSavedQuestionsRef.current = JSON.stringify(result.payload.trainerQuestions);
        setQuestions(result.payload.trainerQuestions);
      }
    });
  };

  const handlePoster = (event, gameId) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      dispatch(
        loadPoster({
          gameId,
          base64String: reader.result,
          onSucces: () => dispatch(getGame({ gameId })),
        }),
      );
    };
  };

  const renderPreview = () => (
    <TrainerMessengerPreview
      title={title}
      theme={title}
      description={description}
      poster={game?.poster}
      previewMode={activeQuestion ? "question" : "welcome"}
      question={activeQuestion}
    />
  );

  const renderSettingsPanel = () => (
    <>
      <section className="trainer-editor__card trainer-editor__card--settings">
        <h3>Настройки тренажёра</h3>
        <TextField
          label="Название тренажёра"
          size="small"
          fullWidth
          sx={fieldSx}
          value={title}
          onChange={(e) => setTitle(sanitizePlainText(e.target.value, 120))}
        />
        <TextField
          label="Приветственный текст"
          size="small"
          fullWidth
          multiline
          minRows={3}
          sx={fieldSx}
          value={description}
          onChange={(e) =>
            setDescription(sanitizePlainText(e.target.value, 2000))
          }
        />
        <div className="trainer-editor__settings-row">
          <TextField
            select
            label="Статус"
            size="small"
            sx={{ ...fieldSx, flex: 1 }}
            value={settings.projectStatus}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                projectStatus: e.target.value,
              }))
            }
          >
            <MenuItem value="draft">Черновик</MenuItem>
            <MenuItem value="published">Опубликован</MenuItem>
            <MenuItem value="hidden">Скрыт</MenuItem>
          </TextField>
          <TextField
            label="Telegram HR"
            size="small"
            sx={{ ...fieldSx, flex: 1.4 }}
            value={settings.author_telegram}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                author_telegram: sanitizePlainText(e.target.value, 80),
              }))
            }
          />
        </div>
        <div className="trainer-editor__poster-row">
          <InputFileUpload
            handleAddPoster={(event) => handlePoster(event, game._id)}
          />
          <span className="trainer-editor__hint">Обложка</span>
        </div>
      </section>
      <ProjectTeamAccess
        gameId={game._id}
        value={availableMessengers}
        onChange={setAvailableMessengers}
        chatAccessSettings={chatAccessGate}
        onChatAccessChange={setChatAccessGate}
      />
    </>
  );

  const renderToolbar = (currentView) => (
    <div className="app-editor__toolbar">
      <Button
        variant="outlined"
        size="small"
        className={`app-editor__tab${
          currentView === "editor" ? " app-editor__tab--active" : ""
        }`}
        startIcon={<EditOutlinedIcon />}
        onClick={() => handleViewChange("editor")}
      >
        Редактор
      </Button>
      <Button
        variant="outlined"
        size="small"
        className={`app-editor__tab${
          currentView === "settings" ? " app-editor__tab--active" : ""
        }`}
        startIcon={<SettingsOutlinedIcon />}
        onClick={() => handleViewChange("settings")}
      >
        Настройки
      </Button>
      <Button
        variant="outlined"
        size="small"
        className={`app-editor__tab${
          currentView === "analytics" ? " app-editor__tab--active" : ""
        }`}
        startIcon={<AnalyticsOutlinedIcon />}
        onClick={() => setView("analytics")}
      >
        Аналитика
      </Button>
      <Button
        variant="contained"
        onClick={() => dispatch(showDialog("delete"))}
        color="error"
        size="small"
        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
      >
        Удалить
      </Button>
    </div>
  );

  const renderMetaBar = () => (
    <div className="trainer-editor__meta-bar">
      <span className="trainer-editor__meta-theme">
        Тренажёр: <strong>{title.trim() || "Без названия"}</strong>
      </span>
      {view === "editor" ? (
        <span className="trainer-editor__meta-count">
          {formatQuestionCount(questions.length)}
        </span>
      ) : null}
    </div>
  );

  if (view === "analytics") {
    return (
      <div className="trainer-editor trainer-editor--analytics">
        {renderToolbar("analytics")}
        <ScenarioAnalytics embedded />
      </div>
    );
  }

  return (
    <div className="trainer-editor">
      {renderToolbar(view)}
      {renderMetaBar()}

      {view === "settings" ? (
        <div className="trainer-editor__workspace trainer-editor__workspace--settings">
          <div className="trainer-editor__settings-column">
            {renderSettingsPanel()}
          </div>
          <aside className="trainer-editor__aside trainer-editor__aside--preview">
            {renderPreview()}
          </aside>
        </div>
      ) : (
        <div className="trainer-editor__workspace">
          <section className="trainer-editor__card trainer-editor__card--questions">
            <div className="trainer-editor__card-head">
              <div>
                <h3>Вопросы и ответы</h3>
                <p className="trainer-editor__card-lead">
                  Основная работа над тренажёром — здесь формируются вопросы и
                  варианты ответов.
                </p>
              </div>
            </div>

          <div className="trainer-editor__questions-layout">
            <div className="trainer-editor__questions-nav">
              {!questions.length ? (
                <p className="trainer-editor__empty">
                  Добавьте первый вопрос — сотрудники будут получать их после
                  «Старт» в боте.
                </p>
              ) : (
                <ul className="trainer-editor__question-list">
                  {questions.map((q, index) => (
                    <li
                      key={q.id}
                      className={`trainer-editor__question-item${
                        activeQuestionId === q.id
                          ? " trainer-editor__question-item--active"
                          : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="trainer-editor__question-btn"
                        onClick={() => setActiveQuestionId(q.id)}
                      >
                        <span className="trainer-editor__question-num">
                          {index + 1}
                        </span>
                        <span className="trainer-editor__question-btn-text">
                          {questionListLabel(q)}
                        </span>
                        <span className="trainer-editor__question-badges">
                          {getQuestionMediaMode(q) === "video" && q.hasVideo ? (
                            <VideocamOutlinedIcon fontSize="inherit" aria-label="Выбрано видео" />
                          ) : null}
                          {getQuestionMediaMode(q) === "image" && q.hasImage ? (
                            <ImageOutlinedIcon fontSize="inherit" aria-label="Выбрана картинка" />
                          ) : null}
                        </span>
                      </button>
                      <Button
                        size="small"
                        variant="text"
                        className="trainer-editor__delete-btn"
                        aria-label={`Удалить вопрос ${index + 1}`}
                        onClick={() =>
                          setQuestionToDelete({
                            id: q.id,
                            label: questionListLabel(q),
                            index: index + 1,
                          })
                        }
                      >
                        Удалить
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="trainer-editor__questions-editor">
              {activeQuestion ? (
                <div className="trainer-editor__question-form">
                  <p className="trainer-editor__hint">
                    Текст вопроса можно дополнить видео или картинкой. Для вопроса
                    выбирается не более одного медиаформата.
                  </p>
                  <TextField
                    label="Текст вопроса (опционально)"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    sx={fieldSx}
                    value={activeQuestion.questionText}
                    onChange={(e) =>
                      updateActiveQuestion({
                        questionText: sanitizePlainText(e.target.value, 2000),
                      })
                    }
                  />

                  <div className="trainer-editor__media-choice">
                    <span className="trainer-editor__answers-label">
                      Медиа к вопросу
                    </span>
                    <ToggleButtonGroup
                      fullWidth
                      exclusive
                      size="small"
                      value={activeQuestionMediaMode}
                      aria-label="Дополнительный формат вопроса"
                      onChange={(_, mode) => {
                        updateActiveQuestion({ questionMode: mode ?? "text" });
                      }}
                    >
                      <ToggleButton value="video">
                        <VideocamOutlinedIcon fontSize="small" />
                        Видео
                      </ToggleButton>
                      <ToggleButton value="image">
                        <ImageOutlinedIcon fontSize="small" />
                        Картинка
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </div>

                  {activeQuestionMediaMode === "video" ? (
                    <div className="trainer-editor__media-block">
                      <span className="trainer-editor__answers-label">Видео</span>
                      <div className="trainer-editor__media-row">
                        <Button
                          variant="outlined"
                          component="label"
                          size="medium"
                          disabled={isUploadingVideo}
                        >
                          {isUploadingVideo
                            ? `Загрузка… ${videoUploadProgress}%`
                            : activeQuestion.hasVideo
                              ? "Заменить видео"
                              : "Загрузить видео"}
                          <input
                            hidden
                            type="file"
                            accept="video/*"
                            disabled={isUploadingVideo}
                            onChange={handleVideoUpload}
                          />
                        </Button>
                        {isUploadingVideo ? (
                          <span className="trainer-editor__media-status">
                            Передано {videoUploadProgress}%…
                          </span>
                        ) : activeQuestion.hasVideo ? (
                          <span className="trainer-editor__media-status">Видео загружено</span>
                        ) : null}
                      </div>
                      <MaxVideoLimitHint availableMessengers={availableMessengers} />
                      {activeQuestion.screenpath ? (
                        <img
                          className="trainer-editor__media-preview"
                          src={getServerMediaUrl(activeQuestion.screenpath)}
                          alt=""
                        />
                      ) : null}
                    </div>
                  ) : null}

                  {activeQuestionMediaMode === "image" ? (
                    <div className="trainer-editor__media-block">
                      <span className="trainer-editor__answers-label">Картинка</span>
                      <InputFileUpload
                        label={activeQuestion.hasImage ? "Заменить картинку" : "Загрузить картинку"}
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                      {activeQuestion.hasImage ? (
                        <span className="trainer-editor__media-status">Картинка загружена</span>
                      ) : null}
                      {activeQuestion.imagepath ? (
                        <img
                          className="trainer-editor__media-preview"
                          src={getServerMediaUrl(activeQuestion.imagepath)}
                          alt=""
                        />
                      ) : null}
                    </div>
                  ) : null}

                  <div className="trainer-editor__answers">
                    <span className="trainer-editor__answers-label">
                      Варианты ответа
                    </span>
                    {activeQuestion.answers.map((answer, answerIndex) => (
                      <div key={answer.id} className="trainer-editor__answer-row">
                        <span className="trainer-editor__answer-num">
                          {answerIndex + 1}
                        </span>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Текст ответа"
                          value={answer.label}
                          onChange={(e) =>
                            updateAnswer(answer.id, {
                              label: sanitizePlainText(e.target.value, 100),
                            })
                          }
                          sx={fieldSx}
                        />
                        <FormControlLabel
                          className="trainer-editor__answer-check"
                          control={
                            <Checkbox
                              checked={Boolean(answer.isCorrect)}
                              onChange={(e) =>
                                updateAnswer(answer.id, {
                                  isCorrect: e.target.checked,
                                })
                              }
                            />
                          }
                          label="Верно"
                        />
                      </div>
                    ))}
                    {activeQuestion.answers.length < 4 ? (
                      <Button size="small" onClick={addAnswer}>
                        + Добавить ответ
                      </Button>
                    ) : null}
                  </div>

                  <Button
                    size="small"
                    variant="contained"
                    className="app-btn app-btn--primary app-editor__add-question-btn trainer-editor__add-submit"
                    startIcon={<AddIcon />}
                    onClick={handleAddQuestion}
                  >
                    Добавить
                  </Button>
                </div>
              ) : (
                <div className="trainer-editor__questions-placeholder trainer-editor__questions-placeholder--action">
                  <p>Выберите вопрос из списка или создайте новый.</p>
                  <Button
                    size="small"
                    variant="contained"
                    className="app-btn app-btn--primary app-editor__add-question-btn"
                    startIcon={<AddIcon />}
                    onClick={handleAddQuestion}
                  >
                    Добавить вопрос
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>

          <aside className="trainer-editor__aside">{renderPreview()}</aside>
        </div>
      )}
      {questionToDelete
        ? createPortal(
            <ConfirmDeleteModal
              open
              title="Удалить вопрос?"
              message={`Вопрос ${questionToDelete.index} «${questionToDelete.label}» и его ответы будут удалены.`}
              detail="Это действие нельзя отменить."
              confirmLabel="Удалить вопрос"
              onClose={() => setQuestionToDelete(null)}
              onConfirm={() => handleDeleteQuestion(questionToDelete.id)}
            />,
            document.body,
          )
        : null}
    </div>
  );
}
